import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet, Repeat, ShieldCheck, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function BudgetTab() {
  const { t } = useI18n();
  const { user } = useAuth();
  const cur = user?.settings?.currency || "USD";
  const [budgets, setBudgets] = useState([]);
  const [active, setActive] = useState(null);
  const [inc, setInc] = useState({ label: "", amount: "" });
  const [exp, setExp] = useState({ label: "", amount: "" });
  const load = () => api.get("/money/budgets").then(({ data }) => setBudgets(data)).catch(() => {});
  useEffect(() => {
    let cancelled = false;

    api.get("/money/budgets")
      .then(({ data }) => {
        if (cancelled) return;
        setBudgets(data);

        if (data.length > 0) {
          api.get(`/money/budgets/${data[0].id}`)
            .then(({ data: budget }) => {
              if (!cancelled) setActive(budget);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, []);
  const openBudget = async (id) => { const { data } = await api.get(`/money/budgets/${id}`); setActive(data); };
  const create = async () => { const { data } = await api.post("/money/budgets", { name: t("money.budget"), currency: cur }); load(); openBudget(data.id); };
  const addIncome = async () => { if (!inc.label || !inc.amount) return; await api.post(`/money/budgets/${active.id}/income`, { label: inc.label, amount: Number(inc.amount) }); setInc({ label: "", amount: "" }); openBudget(active.id); load(); };
  const addExpense = async () => { if (!exp.label || !exp.amount) return; await api.post(`/money/budgets/${active.id}/expense`, { label: exp.label, amount: Number(exp.amount) }); setExp({ label: "", amount: "" }); openBudget(active.id); load(); };
  const delEntry = async (kind, id) => { await api.delete(`/money/entries/${kind}/${id}`); openBudget(active.id); load(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-3">
        <Button className="rounded-full w-full" onClick={create} data-testid="new-budget-btn"><Plus className="h-4 w-4 mr-1" />{t("money.newBudget")}</Button>
        {budgets.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center" data-testid="no-budgets">{t("money.noBudgets")}</p>}
        {budgets.map((b) => (
          <button key={b.id} onClick={() => openBudget(b.id)} data-testid="budget-card"
            className={`w-full text-left rounded-xl border bg-card p-4 transition-colors ${active?.id === b.id ? "border-primary" : "hover:bg-muted"}`}>
            <div className="font-medium">{b.name}</div>
            <div className="text-sm mt-1 flex justify-between"><span className="text-muted-foreground">{t("money.balance")}</span>
              <span className={b.balance >= 0 ? "text-green-600" : "text-destructive"}>{b.balance.toFixed(2)} {b.currency}</span></div>
          </button>
        ))}
      </div>
      <div className="lg:col-span-2">
        {active ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border bg-card p-4"><div className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />{t("money.income")}</div><div className="font-display font-bold text-lg mt-1">{active.total_income.toFixed(0)} {active.currency}</div></div>
              <div className="rounded-xl border bg-card p-4"><div className="text-xs text-muted-foreground flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" />{t("money.expenses")}</div><div className="font-display font-bold text-lg mt-1">{active.total_expense.toFixed(0)} {active.currency}</div></div>
              <div className="rounded-xl border bg-primary/5 p-4"><div className="text-xs text-muted-foreground flex items-center gap-1"><Wallet className="h-3.5 w-3.5" />{t("money.balance")}</div><div className={`font-display font-bold text-lg mt-1 ${active.balance >= 0 ? "text-green-600" : "text-destructive"}`}>{active.balance.toFixed(0)} {active.currency}</div></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border bg-card p-4 space-y-2">
                <h4 className="font-semibold text-sm">{t("money.income")}</h4>
                <div className="flex gap-2"><Input placeholder={t("money.label")} value={inc.label} onChange={(e) => setInc({ ...inc, label: e.target.value })} data-testid="income-label" /><Input type="number" placeholder={t("money.amount")} value={inc.amount} onChange={(e) => setInc({ ...inc, amount: e.target.value })} className="w-24" data-testid="income-amount" /><Button size="icon" className="rounded-full shrink-0" onClick={addIncome} data-testid="add-income-btn"><Plus className="h-4 w-4" /></Button></div>
                {active.incomes?.map((e) => (<div key={e.id} className="flex justify-between text-sm py-1"><span>{e.label}</span><span className="flex items-center gap-2">{e.amount.toFixed(0)}<button onClick={() => delEntry("income", e.id)}><Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" /></button></span></div>))}
              </div>
              <div className="rounded-2xl border bg-card p-4 space-y-2">
                <h4 className="font-semibold text-sm">{t("money.expenses")}</h4>
                <div className="flex gap-2"><Input placeholder={t("money.label")} value={exp.label} onChange={(e) => setExp({ ...exp, label: e.target.value })} data-testid="expense-label" /><Input type="number" placeholder={t("money.amount")} value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} className="w-24" data-testid="expense-amount" /><Button size="icon" className="rounded-full shrink-0" onClick={addExpense} data-testid="add-expense-btn"><Plus className="h-4 w-4" /></Button></div>
                {active.expenses?.map((e) => (<div key={e.id} className="flex justify-between text-sm py-1"><span>{e.label}</span><span className="flex items-center gap-2">{e.amount.toFixed(0)}<button onClick={() => delEntry("expense", e.id)}><Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" /></button></span></div>))}
              </div>
            </div>
          </div>
        ) : <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">{t("money.noBudgets")}</div>}
      </div>
    </div>
  );
}

function RecurringTab() {
  const { t } = useI18n();
  const { user } = useAuth();
  const cur = user?.settings?.currency || "USD";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ label: "", amount: "", frequency: "monthly", next_due: "", category: "" });
  const load = () => api.get("/money/recurring").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!form.label || !form.amount) return; await api.post("/money/recurring", { ...form, amount: Number(form.amount), currency: cur }); setForm({ label: "", amount: "", frequency: "monthly", next_due: "", category: "" }); load(); };
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="rounded-2xl border bg-card p-5 space-y-3">
        <h3 className="font-display font-semibold flex items-center gap-2"><Repeat className="h-4 w-4" />{t("money.addRecurring")}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input placeholder={t("money.label")} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="rec-label" />
          <Input type="number" placeholder={t("money.amount")} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="rec-amount" />
          <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
            <SelectTrigger data-testid="rec-frequency"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="monthly">{t("money.monthly")}</SelectItem><SelectItem value="yearly">{t("money.yearly")}</SelectItem><SelectItem value="weekly">{t("money.weekly")}</SelectItem></SelectContent>
          </Select>
          <Input type="date" value={form.next_due} onChange={(e) => setForm({ ...form, next_due: e.target.value })} data-testid="rec-nextdue" />
        </div>
        <Button className="rounded-full" onClick={add} data-testid="add-recurring-btn"><Plus className="h-4 w-4 mr-1" />{t("common.create")}</Button>
      </div>
      <div className="space-y-2">
        {items.map((r) => (
          <div key={r.id} className="rounded-xl border bg-card p-4 flex items-center justify-between" data-testid="recurring-card">
            <div><div className="font-medium">{r.label}</div><div className="text-xs text-muted-foreground">{t(`money.${r.frequency}`)} {r.next_due && `· ${r.next_due}`}</div></div>
            <div className="flex items-center gap-3"><span className="font-semibold">{r.amount.toFixed(0)} {r.currency}</span>
              <button onClick={async () => { await api.delete(`/money/recurring/${r.id}`); load(); }}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreditCoachTab() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/money/credit-coach").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="rounded-2xl border bg-card p-6">
        <div className="flex items-center gap-3 mb-4"><div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></div>
          <div><h3 className="font-display font-semibold">{data.country_name || t("money.creditCoach")}</h3>
            <p className="text-sm text-muted-foreground">{t("money.creditSystem")}: {data.info?.label}</p></div></div>
        {!data.credit_coach_enabled ? (
          <div className="rounded-xl bg-muted p-4 text-sm text-muted-foreground flex gap-2" data-testid="coach-disabled"><Info className="h-4 w-4 shrink-0 mt-0.5" />{t("money.coachDisabled")}</div>
        ) : (
          <div>
            {data.info?.range && data.info.range !== "N/A" && <p className="text-sm mb-3"><span className="font-medium">Score:</span> {data.info.range}</p>}
            <h4 className="font-semibold text-sm mb-2">{t("money.tips")}</h4>
            <ul className="space-y-2">{data.info?.tips?.map((tip, i) => (<li key={i} className="text-sm flex gap-2" data-testid={`coach-tip-${i}`}><span className="text-primary">•</span>{tip}</li>))}</ul>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-4 pt-4 border-t">{data.disclaimer}</p>
      </div>
    </div>
  );
}

export default function Money() {
  const { t } = useI18n();
  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader title={t("money.title")} subtitle={t("money.subtitle")} />
      <Tabs defaultValue="budget">
        <TabsList className="mb-6">
          <TabsTrigger value="budget" data-testid="tab-budget"><Wallet className="h-4 w-4 mr-1.5" />{t("money.budget")}</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring"><Repeat className="h-4 w-4 mr-1.5" />{t("money.recurring")}</TabsTrigger>
          <TabsTrigger value="coach" data-testid="tab-coach"><ShieldCheck className="h-4 w-4 mr-1.5" />{t("money.creditCoach")}</TabsTrigger>
        </TabsList>
        <TabsContent value="budget"><BudgetTab /></TabsContent>
        <TabsContent value="recurring"><RecurringTab /></TabsContent>
        <TabsContent value="coach"><CreditCoachTab /></TabsContent>
      </Tabs>
    </div>
  );
}
